import * as React from "react";
import {
    unstable_composeClasses as composeClasses,
    generateUtilityClass,
    generateUtilityClasses,
} from "@mui/material";
import type {OverridableComponent} from "@mui/material/OverridableComponent";
import Radio, {type RadioProps} from "../Radio";
import {type ButtonProps} from "../Button";
import {capitalize} from "../../utils";
import {useId, useForkRef} from "../../hooks";
import {useThemeProps, alpha, getContrastRatio, styled} from "../../theme";
import Typography, {type TypographyProps} from "../Typography";

interface SegmentControlButtonLabelProps
    extends Omit<TypographyProps, "ref">,
        Omit<React.HTMLAttributes<HTMLLabelElement>, keyof TypographyProps> {
    ref?: React.Ref<HTMLLabelElement>;
}

export interface SegmentControlButtonProps extends Omit<RadioProps, "size" | "color"> {
    /**
     * The size of the component.
     * `small` is equivalent to the dense button styling.
     * @default 'medium'
     */
    size?: ButtonProps["size"];
    /**
     * The content of the component.
     */
    children: React.ReactNode;
    /**
     * Props forwarded to the Typography component
     * @deprecated use slotProps instead
     */
    typographyProps?: SegmentControlButtonLabelProps;
    /**
     * The props used for each slot inside.
     */
    slotProps?: {
        label?: SegmentControlButtonLabelProps;
    };
    /**
     * Override or extend the styles applied to the component.
     */
    classes?: Partial<SegmentControlButtonClasses>;
}

export type SegmentControlButtonClasses = {
    /** Styles applied to the root element. */
    root: string;
    /** Styles applied to the label element. */
    label: string;
    /** Styles applied to the input element. */
    input: string;
    /** Styles applied to the root element when disabled. */
    disabled: string;
    /** Styles applied to the root element if `checked={true}` */
    checked: string;
    /** Styles applied to the label element if `size="small" */
    sizeSmall: string;
    /** Styles applied to the label element if `size="medium" */
    sizeMedium: string;
    /** Styles applied to the label element if `size="large" */
    sizeLarge: string;
};

const getSegmentControlUtilityClasses = (slot: string) =>
    generateUtilityClass("MuiSegmentControlButton", slot);
export const segmentControlButtonClasses: SegmentControlButtonClasses =
    generateUtilityClasses("MuiSegmentControlButton", [
        "root",
        "label",
        "input",
        "disabled",
        "checked",
        "sizeSmall",
        "sizeMedium",
        "sizeLarge",
    ]);

/**
 * A Radio component styled as a Button. To be used as a child of `<SegmentControl />`.
 *
 * Storybook:
 * - [SegmentControl](https://storybook.dev.atg.se/?path=/story/ui-toolkit-components-segmentcontrol--default)
 */
const SegmentControlButtonRoot = styled(
    Radio as (props: Omit<SegmentControlButtonProps, "children">) => JSX.Element,
    {
        shouldForwardProp: (prop) => !["children", "size"].includes(prop as string),
        name: "MuiSegmentControlButton",
        slot: "Root",
        overridesResolver: (props, styles) => [
            styles.root,
            props.checked && styles.checked,
            props.disabled && styles.disabled,
            styles[`size${capitalize(props.size)}`],
        ],
    },
)(({theme, disabled, size}) => ({
    padding: "6px 16px",
    borderRadius: theme.shape.borderRadius,
    ...(disabled && {
        opacity: theme.palette.action.disabledOpacity,
    }),

    "& .MuiTouchRipple-root": {
        color: theme.palette.action.active,
    },

    "&:hover": {
        backgroundColor: alpha(
            theme.palette.action.active,
            theme.palette.action.hoverOpacity,
        ),
    },

    "@media (hover: none)": {
        backgroundColor: "transparent",
    },

    "&.Mui-checked": {
        backgroundColor: theme.palette.common.white,
        boxShadow: theme.shadows[2],
    },

    ...(size === "small" && {
        padding: "4px 5px",
    }),

    ...(size === "large" && {
        padding: "8px 11px",
    }),
}));

const SegmentControlButtonLabel = styled(
    Typography as OverridableComponent<{
        props: TypographyProps;
        defaultComponent: "label";
    }>,
    {
        name: "MuiSegmentControlButton",
        slot: "Label",
        overridesResolver: (_, styles) => [styles.label],
    },
)<Pick<SegmentControlButtonProps, "size">>(({theme, size}) => ({
    color:
        getContrastRatio(
            theme.palette.action.disabledBackground,
            alpha(theme.palette.common.white, 0.87),
        ) >= 3
            ? theme.palette.common.black
            : theme.palette.common.white,
    fontSize: theme.typography.pxToRem(14),

    [`.${segmentControlButtonClasses.sizeSmall} &`]: {
        fontSize: theme.typography.pxToRem(13),
    },

    ...(size === "large" && {
        fontSize: theme.typography.pxToRem(15),
    }),

    [`.${segmentControlButtonClasses.checked} &`]: {
        color: theme.palette.common.black,
    },
}));

// const Label = SegmentControlButtonLabel.withComponent("label");

/**
 * A Radio component styled as a Button. To be used as a child of `<SegmentControl />`.
 */
const SegmentControlButton = React.forwardRef<
    HTMLButtonElement,
    SegmentControlButtonProps
>((inProps, ref) => {
    const props = useThemeProps({props: inProps, name: "MuiSegmentControlButton"});
    const {
        "aria-controls": ariaControls = undefined,
        checked,
        checkedIcon: checkedIconProp,
        children,
        classes: classesProp,
        disabled,
        icon: iconProp,
        id: idProp,
        role = undefined,
        size = "medium",
        typographyProps,
        slotProps,
        inputProps,
        value,
        ...rest
    } = props;
    const id = useId(idProp);
    const classes = composeClasses(
        {
            root: [
                "root",
                checked && "checked",
                disabled && "disabled",
                `size${capitalize(size)}`,
            ],
            label: ["label"],
            input: ["input"],
        },
        getSegmentControlUtilityClasses,
        classesProp,
    );

    const controlButtonRef = React.useRef<HTMLButtonElement>(null);
    const forkedRef = useForkRef(ref, controlButtonRef);

    const icon = (
        <SegmentControlButtonLabel
            className={classes.label}
            variant="button"
            component="label"
            htmlFor={id}
            size={size}
            {...typographyProps}
            {...slotProps?.label}
        >
            {children}
        </SegmentControlButtonLabel>
    );

    let ariaProps = {};

    if (role === "tab") {
        const isChecked =
            (checked ?? controlButtonRef?.current?.classList.contains("Mui-checked")) ||
            false;
        ariaProps = {
            "aria-selected": String(isChecked),
            "aria-controls": ariaControls,
        };
    }

    const {
        role: inputPropsRole,
        className: inputPropsClassname,
        ...restInputProps
    } = inputProps ?? {};

    return (
        <SegmentControlButtonRoot
            className={classes.root}
            checked={checked}
            checkedIcon={checkedIconProp || icon}
            disabled={disabled}
            icon={iconProp || icon}
            id={id}
            inputProps={{
                role: inputPropsRole ?? role,
                className: classes.input.concat(" ", inputPropsClassname || ""),
                ...restInputProps,
                ...ariaProps,
            }}
            ref={forkedRef}
            value={value}
            size={size}
            {...rest}
        />
    );
});

export default SegmentControlButton;
